import React, { useState } from 'react';
import styled from '@emotion/styled';

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 8px 0;
`;

const ModalSubtitle = styled.p`
  color: #718096;
  margin: 0 0 16px 0;
  font-size: 14px;
  line-height: 1.5;
`;

const ModalBody = styled.div`
  padding: 0 24px 24px 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
`;

const RequiredLabel = styled(Label)`
  &:after {
    content: " *";
    color: #e53e3e;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
  }
  
  &:invalid {
    border-color: #e53e3e;
  }
`;

const SlugPreview = styled.div`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  color: #4a5568;
  margin-top: 4px;
`;

const InfoBox = styled.div`
  background: #ebf8ff;
  border: 1px solid #bee3f8;
  border-radius: 6px;
  padding: 12px;
  font-size: 14px;
  color: #2c5282;
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
  
  ${({ variant = 'secondary' }) => {
    if (variant === 'primary') {
      return `
        background: linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%);
        color: white;
        &:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(255, 107, 107, 0.4);
        }
        &:disabled {
          background: #a0aec0;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }
      `;
    }
    return `
      background: white;
      color: #4a5568;
      border-color: #e2e8f0;
      &:hover:not(:disabled) {
        background: #f7fafc;
      }
    `;
  }}
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #742a2a;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #e53e3e;
  font-size: 14px;
  margin-bottom: 16px;
`;

const SuccessMessage = styled.div`
  background: #c6f6d5;
  color: #22543d;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #38a169;
  font-size: 14px;
  margin-bottom: 16px;
`;

const PageLink = styled.a`
  color: #ff6b6b;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

interface SavePageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (slug: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  success?: boolean;
  savedPageUrl?: string;
}

export const SavePageModal: React.FC<SavePageModalProps> = ({
  isOpen,
  onClose,
  onSave,
  isLoading = false,
  error = null,
  success = false,
  savedPageUrl
}) => {
  const [slug, setSlug] = useState('');

  const generateSlugFromTitle = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/--+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = generateSlugFromTitle(e.target.value);
    setSlug(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug.trim()) return;
    
    await onSave(slug.trim());
  };

  const handleClose = () => {
    if (!isLoading) {
      setSlug('');
      onClose();
    }
  };

  const baseUrl = window.location.origin;

  return (
    <Overlay isOpen={isOpen} onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Save Website</ModalTitle>
          <ModalSubtitle>
            Choose a unique URL path for your website. This will be the address where visitors can access your page.
          </ModalSubtitle>
        </ModalHeader>
        
        <ModalBody>
          {error && (
            <ErrorMessage>
              {error}
            </ErrorMessage>
          )}
          
          {success && savedPageUrl && (
            <SuccessMessage>
              Website saved successfully! Your page is now live at: <br />
              <PageLink href={savedPageUrl} target="_blank" rel="noopener noreferrer">
                {savedPageUrl}
              </PageLink>
            </SuccessMessage>
          )}
          
          {!success && (
            <Form onSubmit={handleSubmit}>
              <InfoBox>
                <strong>What is a URL slug?</strong><br />
                The slug is the part of the URL that comes after your domain name. 
                For example, if your slug is "my-business", your page will be accessible at:
                <br /><br />
                <code>{baseUrl}/pages/my-business</code>
              </InfoBox>
              
              <FormGroup>
                <RequiredLabel htmlFor="slug">URL Slug</RequiredLabel>
                <Input
                  id="slug"
                  type="text"
                  value={slug}
                  onChange={handleSlugChange}
                  placeholder="my-awesome-website"
                  pattern="[a-z0-9-]+"
                  title="Only lowercase letters, numbers, and hyphens are allowed"
                  required
                  disabled={isLoading}
                />
                {slug && (
                  <SlugPreview>
                    Your page will be available at: <strong>{baseUrl}/pages/{slug}</strong>
                  </SlugPreview>
                )}
              </FormGroup>
              
              <ButtonGroup>
                <Button type="button" onClick={handleClose} disabled={isLoading}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={isLoading || !slug.trim()}>
                  {isLoading ? 'Saving...' : 'Save Website'}
                </Button>
              </ButtonGroup>
            </Form>
          )}
          
          {success && (
            <ButtonGroup>
              <Button type="button" onClick={handleClose}>
                Close
              </Button>
            </ButtonGroup>
          )}
        </ModalBody>
      </Modal>
    </Overlay>
  );
};